import { adminApi } from "../../lib/axios";

export interface PlanFeatureItem {
  id: string;
  name: string;
  isActive: boolean;
}

export interface PlanLimits {
  approvedReferralsPerMonth: number | "unlimited";
  maxAffiliates: number | "unlimited";
  maxPrograms: number | "unlimited";
}

export interface PlanDescriptionItem {
  id: string;
  description: string;
  order: number;
}

export interface PlanAssignment {
  id: string;
  sessionId: string;
  shop: string;
  companyName: string | null;
  createdAt: string;
}

export interface AdminPlan {
  id: string;
  name: string;
  price: number;
  // Fraction, e.g. 0.01 = 1% revenue share
  commissionRate: number;
  isActive: boolean;
  isPublic: boolean;
  aiSearchQuestions: number | null;
  subscriberCount: number;
  features: PlanFeatureItem[];
  limits: PlanLimits | null;
  descriptions: PlanDescriptionItem[];
  assignments: PlanAssignment[];
}

export type EnterpriseRequestStatus = "NEW" | "CONTACTED" | "CLOSED";

/** A brand asking for a custom deal from the Enterprise card on the pricing page */
export interface EnterprisePlanRequest {
  id: string;
  sessionId: string;
  shop: string;
  contactName: string;
  email: string;
  phone: string | null;
  companyName: string | null;
  monthlyRevenue: string | null;
  currentPlan: string | null;
  message: string | null;
  status: EnterpriseRequestStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePlanInput {
  name: string;
  price: number;
  commissionRate?: number;
  isPublic?: boolean;
  aiSearchQuestions?: number | null;
  copyFromPlanId?: string;
}

export interface UpdatePlanInput {
  name?: string;
  price?: number;
  commissionRate?: number;
  isActive?: boolean;
  isPublic?: boolean;
  aiSearchQuestions?: number | null;
}

export const adminPlansService = {
  async getAllPlans(): Promise<AdminPlan[]> {
    const { data } = await adminApi.get("/admin/plan-features/plans");
    return data;
  },

  async createPlan(input: CreatePlanInput) {
    const { data } = await adminApi.post("/admin/plan-features/plans", input);
    return data;
  },

  async updatePlan(planId: string, input: UpdatePlanInput) {
    const { data } = await adminApi.put(
      `/admin/plan-features/plans/${planId}`,
      input,
    );
    return data;
  },

  async getAssignments(planId: string): Promise<{ assignments: PlanAssignment[] }> {
    const { data } = await adminApi.get(
      `/admin/plan-features/plans/${planId}/assignments`,
    );
    return data;
  },

  async assignBrand(
    planId: string,
    params: { sessionId?: string; brandId?: string },
  ): Promise<{ success: boolean; assignment: PlanAssignment }> {
    const { data } = await adminApi.post(
      `/admin/plan-features/plans/${planId}/assignments`,
      params,
    );
    return data;
  },

  async removeAssignment(planId: string, assignmentId: string) {
    const { data } = await adminApi.delete(
      `/admin/plan-features/plans/${planId}/assignments/${assignmentId}`,
    );
    return data;
  },

  async getEnterpriseRequests(): Promise<EnterprisePlanRequest[]> {
    const { data } = await adminApi.get(
      "/admin/plan-features/enterprise-requests",
    );
    return data;
  },

  async updateEnterpriseRequestStatus(
    requestId: string,
    status: EnterpriseRequestStatus,
  ) {
    const { data } = await adminApi.put(
      `/admin/plan-features/enterprise-requests/${requestId}/status`,
      { status },
    );
    return data;
  },
};
